import { useState, useEffect } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { createApiClient } from '@mybusinessmate/api-client';
import { ensureSubdomain } from '../../../utils/subdomain';

const apiClient = createApiClient(import.meta.env.VITE_API_URL || 'http://localhost:8787');

export const Route = createFileRoute('/subdomain/dev/')({
  beforeLoad: ensureSubdomain('dev'),
  component: DevPortalPage,
});

interface InfraState {
  status: 'loading' | 'loaded' | 'error';
  data?: {
    timestamp: string;
    environment: string;
    infrastructure: Record<string, { status: string; message?: string }>;
  };
}

function DevPortalPage() {
  const [activeTab, setActiveTab] = useState<'visualizer' | 'contracts' | 'api'>('visualizer');
  const [infra, setInfra] = useState<InfraState>({ status: 'loading' });

  useEffect(() => {
    if (activeTab === 'api' && infra.status === 'loading') {
      apiClient.api.infra.status
        .$get()
        // Infrastructure details are admin-only now: signed out (or not an admin) shows the error state.
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
        .then((data) => setInfra({ status: 'loaded', data }))
        .catch(() => setInfra({ status: 'error' }));
    }
  }, [activeTab, infra.status]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans">
      {/* Dev Hub Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-slate-950/90 border-b border-slate-800/80 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center font-bold text-white shadow-md shadow-emerald-600/30">
              🛠️
            </div>
            <div>
              <span className="font-bold text-sm tracking-tight text-white">Developer Hub &amp; DB Visualizer</span>
              <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                dev.mybusinessmate.ai
              </span>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center gap-1 bg-slate-900 border border-slate-800 rounded-xl p-1 text-xs">
            <button
              onClick={() => setActiveTab('visualizer')}
              className={`px-3 py-1.5 rounded-lg font-medium transition ${
                activeTab === 'visualizer'
                  ? 'bg-emerald-500 text-slate-950 font-bold shadow'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              📊 DB Visualizer (49 Tables)
            </button>
            <button
              onClick={() => setActiveTab('contracts')}
              className={`px-3 py-1.5 rounded-lg font-medium transition ${
                activeTab === 'contracts'
                  ? 'bg-emerald-500 text-slate-950 font-bold shadow'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              📦 Monorepo Contracts
            </button>
            <button
              onClick={() => setActiveTab('api')}
              className={`px-3 py-1.5 rounded-lg font-medium transition ${
                activeTab === 'api'
                  ? 'bg-emerald-500 text-slate-950 font-bold shadow'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              ⚡ Cloud Edge (R2, QStash, Redis)
            </button>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/subdomain/dev/graph"
              className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-xs font-semibold text-slate-200 border border-slate-800 hover:border-slate-700 transition flex items-center gap-1.5"
            >
              <span>🕸️ Knowledge Graph</span>
              <span>→</span>
            </Link>
            <a
              href="/db-visualizer.html"
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 border border-slate-700 transition flex items-center gap-1.5"
            >
              <span>Full Screen DB</span>
              <span>↗</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col">
        {activeTab === 'visualizer' && (
          <div className="flex-1 flex flex-col w-full h-[calc(100vh-61px)]">
            <div className="px-6 py-2 bg-slate-900/60 border-b border-slate-800 text-xs text-slate-400 flex items-center justify-between">
              <span>Interactive 3NF/BCNF PostgreSQL schema visualizer with force-directed network graph</span>
              <span className="font-mono text-emerald-400">49 Tables • 41 Enums • 78 Foreign Keys • 24 Triggers</span>
            </div>
            <iframe
              src="/db-visualizer.html"
              title="PostgreSQL Interactive Database Architecture Visualizer"
              className="flex-1 w-full border-0 bg-[#090d16]"
            />
          </div>
        )}

        {activeTab === 'contracts' && (
          <div className="max-w-5xl w-full mx-auto px-6 py-8 space-y-6">
            <h2 className="text-xl font-bold text-white">Monorepo Cross-Cutting Packages</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-sky-500/10 text-sky-400 border border-sky-500/20">
                  @mybusinessmate/db
                </span>
                <h3 className="font-bold text-white">Drizzle ORM &amp; Neon Serverless</h3>
                <p className="text-xs text-slate-400">
                  Transcribed 49 normalized tables and 41 enums. Strict references to agency_staff(id).
                </p>
                <div className="text-[11px] font-mono text-slate-400 pt-2 border-t border-slate-800">
                  Location: packages/db/src/schema/
                </div>
              </div>

              <div className="p-5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                  @mybusinessmate/schema
                </span>
                <h3 className="font-bold text-white">Universal Zod Validation</h3>
                <p className="text-xs text-slate-400">
                  Pure TypeScript/Zod schemas with zero database drivers or Node dependencies. Shared by web and mobile.
                </p>
                <div className="text-[11px] font-mono text-slate-400 pt-2 border-t border-slate-800">
                  Location: packages/schema/src/index.ts
                </div>
              </div>

              <div className="p-5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-purple-500/10 text-purple-400 border border-purple-500/20">
                  @mybusinessmate/api-client
                </span>
                <h3 className="font-bold text-white">End-to-End Type-Safe RPC</h3>
                <p className="text-xs text-slate-400">
                  Hono RPC client (hc) binding directly to apps/api AppType. Zero manual OpenAPI typing.
                </p>
                <div className="text-[11px] font-mono text-slate-400 pt-2 border-t border-slate-800">
                  Location: packages/api-client/src/index.ts
                </div>
              </div>

              <div className="p-5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  @mybusinessmate/ui-tokens
                </span>
                <h3 className="font-bold text-white">Design Tokens &amp; Badges</h3>
                <p className="text-xs text-slate-400">
                  Role badges, task review statuses, brand colors. Pure TS consumable on Web (CSS/Tailwind) &amp; Mobile
                  (React Native).
                </p>
                <div className="text-[11px] font-mono text-slate-400 pt-2 border-t border-slate-800">
                  Location: packages/ui-tokens/src/index.ts
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'api' && (
          <div className="max-w-5xl w-full mx-auto px-6 py-8 space-y-6">
            <h2 className="text-xl font-bold text-white">Cloud Infrastructure Engine (S1-03)</h2>

            {/* Live Infra Status Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Upstash Redis</span>
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-mono ${
                      infra.data?.infrastructure.redis?.status === 'healthy'
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    }`}
                  >
                    {infra.data?.infrastructure.redis?.status || 'Connecting...'}
                  </span>
                </div>
                <div className="text-sm font-bold text-white">Sliding-Window Rate Limiter</div>
                <p className="text-xs text-slate-400">
                  {infra.data?.infrastructure.redis?.message || 'Configured via UPSTASH_REDIS_REST_URL'}
                </p>
                <div className="text-[10px] font-mono text-slate-500 pt-1">Limit: 120 req/min per IP</div>
              </div>

              <div className="p-5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Upstash QStash</span>
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-mono ${
                      infra.data?.infrastructure.qstash?.status === 'healthy'
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    }`}
                  >
                    {infra.data?.infrastructure.qstash?.status || 'Connecting...'}
                  </span>
                </div>
                <div className="text-sm font-bold text-white">Serverless Message Broker</div>
                <p className="text-xs text-slate-400">
                  {infra.data?.infrastructure.qstash?.message || 'Asynchronous Job Dispatch & Webhooks'}
                </p>
                <div className="text-[10px] font-mono text-slate-500 pt-1">Retries with Exponential Backoff</div>
              </div>

              <div className="p-5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cloudflare R2</span>
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-mono ${
                      infra.data?.infrastructure.r2?.status === 'healthy'
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    }`}
                  >
                    {infra.data?.infrastructure.r2?.status || 'Connecting...'}
                  </span>
                </div>
                <div className="text-sm font-bold text-white">S3-Compatible Object Vault</div>
                <p className="text-xs text-slate-400">
                  {infra.data?.infrastructure.r2?.message || 'Deliverables, Logos & Receipts'}
                </p>
                <div className="text-[10px] font-mono text-slate-500 pt-1">Pre-Signed Direct Uploads</div>
              </div>
            </div>

            {/* Endpoints Table */}
            <div className="p-5 rounded-xl bg-slate-900 border border-slate-800 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Active API Endpoints
                </span>
                <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-emerald-500/10 text-emerald-400">
                  Hono Edge Router
                </span>
              </div>
              <div className="space-y-2 font-mono text-xs">
                <div className="p-3 rounded-lg bg-slate-950 flex items-center justify-between">
                  <span className="text-emerald-400">GET /api/infra/status</span>
                  <span className="text-slate-400">Live diagnostics for Redis, QStash &amp; R2</span>
                </div>
                <div className="p-3 rounded-lg bg-slate-950 flex items-center justify-between">
                  <span className="text-sky-400">POST /api/storage/presigned-upload</span>
                  <span className="text-slate-400">Generate S3 PUT pre-signed URL (15m expiry)</span>
                </div>
                <div className="p-3 rounded-lg bg-slate-950 flex items-center justify-between">
                  <span className="text-sky-400">POST /api/storage/presigned-download</span>
                  <span className="text-slate-400">Generate authenticated download URL (1h expiry)</span>
                </div>
                <div className="p-3 rounded-lg bg-slate-950 flex items-center justify-between">
                  <span className="text-purple-400">POST /api/jobs/dispatch</span>
                  <span className="text-slate-400">Enqueue background job via QStash</span>
                </div>
                <div className="p-3 rounded-lg bg-slate-950 flex items-center justify-between">
                  <span className="text-purple-400">POST /api/jobs/webhook</span>
                  <span className="text-slate-400">QStash webhook receiver with signature validation</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
